/** Shared helpers for current-user profile fields from v1 API. */

import { getSessionStore } from '../store/sessionStore';

/** Coerce API/local skills to a string list; repair legacy char-split rows. */
export function normalizeSkillsList(value: unknown): string[] {
  if (value == null) {
    return [];
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) {
      return [];
    }
    if (text.startsWith('[')) {
      try {
        const parsed = JSON.parse(text) as unknown;
        if (Array.isArray(parsed)) {
          return normalizeSkillsList(parsed);
        }
      } catch {
        /* fall through */
      }
    }
    if (text.includes(',')) {
      return repairCharSplitSkillItems(
        text
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean)
      );
    }
    return [text];
  }
  if (Array.isArray(value)) {
    const items = value.map((item) => (item == null ? '' : String(item).trim())).filter(Boolean);
    return repairCharSplitSkillItems(items);
  }
  return [];
}

function repairCharSplitSkillItems(items: string[]): string[] {
  if (!items.length) {
    return [];
  }
  if (items.length > 1 && items.every((part) => part.length === 1)) {
    return [items.join('')];
  }
  const repaired: string[] = [];
  let buffer: string[] = [];
  for (const part of items) {
    if (part.length === 1) {
      buffer.push(part);
      continue;
    }
    if (buffer.length) {
      repaired.push(buffer.join(''));
      buffer = [];
    }
    repaired.push(part);
  }
  if (buffer.length) {
    repaired.push(buffer.join(''));
  }
  return repaired;
}

/** Saved prompts required before the candidate can use the dashboard. */
export const MIN_SUBMITTED_PROMPTS = 4;

export function promptHasUploadedVideo(prompt: unknown): boolean {
  if (!prompt || typeof prompt !== 'object') {
    return false;
  }
  const url = String((prompt as { fileUrl?: string }).fileUrl ?? '').trim();
  return Boolean(url && url !== 'N/A');
}

/** Profile sharing requires the minimum prompt count and a video on each prompt. */
export function canShareProfile(prompts: unknown[] | null | undefined): boolean {
  const list = Array.isArray(prompts) ? prompts : [];
  if (list.length < MIN_SUBMITTED_PROMPTS) {
    return false;
  }
  return list.every(promptHasUploadedVideo);
}

export function shareProfileBlockMessage(prompts: unknown[] | null | undefined): string {
  const list = Array.isArray(prompts) ? prompts : [];
  if (list.length < MIN_SUBMITTED_PROMPTS) {
    const remaining = MIN_SUBMITTED_PROMPTS - list.length;
    if (list.length === 0) {
      return `You need at least ${MIN_SUBMITTED_PROMPTS} screening prompts with videos before sharing your profile.`;
    }
    return `You need at least ${MIN_SUBMITTED_PROMPTS} screening prompts with videos before sharing (${remaining} more to go).`;
  }
  return 'Your screening prompts are incomplete. Please add videos before sharing.';
}

/** Parse linkedin + otherSocial into a deduped list of profile links. */
export function parseProfessionalLinksFromUser(
  user: Record<string, unknown> | null | undefined
): string[] {
  if (!user) return [];
  const links: string[] = [];
  const linkedin = String(user.linkedin ?? '').trim();
  if (linkedin) links.push(linkedin);

  const rawOther = user.otherSocial ?? user.other_social;
  if (Array.isArray(rawOther)) {
    rawOther.forEach((entry) => {
      const value = String(entry ?? '').trim();
      if (value) links.push(value);
    });
  } else if (typeof rawOther === 'string' && rawOther.trim()) {
    try {
      const parsed = JSON.parse(rawOther) as unknown;
      if (Array.isArray(parsed)) {
        parsed.forEach((entry) => {
          const value = String(entry ?? '').trim();
          if (value) links.push(value);
        });
      } else {
        links.push(rawOther.trim());
      }
    } catch {
      links.push(rawOther.trim());
    }
  }

  return Array.from(new Set(links));
}

const PROMPT_SETUP_COMPLETE_KEY = 'ss:promptSetupComplete';
const PROMPT_SETUP_V2_KEY = 'ss:promptSetupV2';
const PROMPT_SETUP_V2_AT_COUNT_KEY = 'ss:promptSetupV2AtCount';

/** Opt into draft-save vs final-submit prompt onboarding (set on create-prompt visit). */
export function enablePromptSetupV2Flow(userId?: string | number, promptCountAtEnroll = 0): void {
  try {
    localStorage.setItem(PROMPT_SETUP_V2_KEY, userId != null ? String(userId) : '1');
    localStorage.setItem(PROMPT_SETUP_V2_AT_COUNT_KEY, String(Math.max(0, promptCountAtEnroll)));
  } catch {
    /* ignore quota errors */
  }
}

export function isPromptSetupV2Enabled(userId?: string | number): boolean {
  try {
    const stored = localStorage.getItem(PROMPT_SETUP_V2_KEY);
    if (!stored) {
      return false;
    }
    if (userId != null) {
      return stored === String(userId);
    }
    return stored === '1' || stored.length > 0;
  } catch {
    return false;
  }
}

/** Set only after consent + submit on the create-prompt summary step. */
export function markPromptSetupComplete(userId?: string | number): void {
  try {
    localStorage.setItem(PROMPT_SETUP_COMPLETE_KEY, userId != null ? String(userId) : 'true');
    localStorage.setItem(PROMPT_SETUP_V2_KEY, '1');
  } catch {
    /* ignore quota errors */
  }
}

/** 3-step profile form submitted (backend: basic_profile_completed). */
export function isBasicProfileCompleted(user?: Record<string, unknown> | null): boolean {
  if (!user) {
    return false;
  }
  return user.basicProfileCompleted === true || user.basic_profile_completed === true;
}

/** Full onboarding done — prompts submitted on summary step (backend: onboarding_completed). */
export function isFullOnboardingCompleted(user?: Record<string, unknown> | null): boolean {
  if (!user) {
    return false;
  }
  return user.onboardingCompleted === true || user.onboarding_completed === true;
}

/** @deprecated Use isFullOnboardingCompleted — kept for existing call sites. */
export function isPromptSetupComplete(user?: Record<string, unknown> | null): boolean {
  return isFullOnboardingCompleted(user);
}

/** Clear completion on logout; keep per-user v2 flag so draft saves still require submit. */
export function clearPromptSetupState(): void {
  try {
    localStorage.removeItem(PROMPT_SETUP_COMPLETE_KEY);
  } catch {
    /* ignore quota errors */
  }
}

export const PENDING_ROLE_CHANGE_KEY = 'ss:pendingRoleChange';

/** User confirmed they want to change job role — prompts delete after new role is saved. */
export function markPendingRoleChange(): void {
  try {
    sessionStorage.setItem(PENDING_ROLE_CHANGE_KEY, '1');
  } catch {
    /* ignore quota errors */
  }
}

export function isPendingRoleChange(search = ''): boolean {
  try {
    const query = search || window.location.search;
    if (query.includes('changeRole=1')) {
      return true;
    }
    return sessionStorage.getItem(PENDING_ROLE_CHANGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearPendingRoleChange(): void {
  try {
    sessionStorage.removeItem(PENDING_ROLE_CHANGE_KEY);
  } catch {
    /* ignore quota errors */
  }
}

/** Route to step 1 of create-prompt (industry / title / level). */
export function roleSelectionPath(): string {
  return '/create-prompt?changeRole=1';
}

/** Persist flag and navigate to the role-selection flow. */
export function startRoleSelectionFlow(navigate: (path: string) => void): void {
  markPendingRoleChange();
  navigate(roleSelectionPath());
}

export const ROLE_CHANGE_WARNING =
  'Changing your job role will delete your current screening prompts. Your resume, portfolio, references, and other profile sections will stay as they are, and you can update those sections anytime from your profile page. You will choose your new role next and record new prompts for that role.';

const ONBOARDING_SETUP_PREFIXES = [
  '/onboarding',
  '/get-started',
  '/create-prompt',
  '/complete-profile',
  '/complete-profile-avatar',
];

export function isOnboardingSetupRoute(pathname: string): boolean {
  return ONBOARDING_SETUP_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/** Phase 1 — 3-step profile onboarding modal. */
export function isOnboardingFormRoute(pathname: string): boolean {
  return (
    pathname === '/onboarding' ||
    pathname.startsWith('/onboarding/') ||
    pathname === '/complete-profile' ||
    pathname.startsWith('/complete-profile/') ||
    pathname === '/complete-profile-avatar' ||
    pathname.startsWith('/complete-profile-avatar/')
  );
}

/** Getting Started hub between profile onboarding and prompt setup. */
export function isGetStartedRoute(pathname: string): boolean {
  return pathname === '/get-started' || pathname.startsWith('/get-started/');
}

/** Phase 2 — screening prompts wizard (after profile onboarding). */
export function isPromptSetupRoute(pathname: string): boolean {
  return pathname === '/create-prompt' || pathname.startsWith('/create-prompt/');
}

/** Routes allowed while the candidate still needs screening prompts. */
export function isPostOnboardingSetupRoute(pathname: string): boolean {
  return isGetStartedRoute(pathname) || isPromptSetupRoute(pathname);
}

export type CandidateJourneyPhase = 'onboarding' | 'prompt-setup' | 'ready';

/** Single source of truth for where a candidate is in first-run setup. */
export function resolveCandidateJourneyPhase(
  user: Record<string, unknown> | null | undefined,
  promptCount: number
): CandidateJourneyPhase {
  if (userNeedsOnboarding(user)) {
    return 'onboarding';
  }
  if (userNeedsPromptSetup(promptCount, user)) {
    return 'prompt-setup';
  }
  return 'ready';
}

/** Redirect target for a route given journey phase, or null when route is allowed. */
export function resolveCandidateJourneyRedirect(
  pathname: string,
  phase: CandidateJourneyPhase,
  search = ''
): string | null {
  if (isPendingRoleChange(search) && isPromptSetupRoute(pathname)) {
    return null;
  }

  switch (phase) {
    case 'onboarding':
      return isOnboardingFormRoute(pathname) ? null : '/onboarding';
    case 'prompt-setup':
      // Legacy mid-funnel users (old profile done, prompts pending) now use Fast Onboarding.
      return isOnboardingFormRoute(pathname) ? null : '/onboarding';
    case 'ready':
      return isOnboardingSetupRoute(pathname) ? '/dashboard' : null;
    default:
      return null;
  }
}

export function normalizeApiUserRecord(
  apiUser: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!apiUser || typeof apiUser !== 'object') {
    return null;
  }
  return mergeProfileFormUser(apiUser);
}

/** Keep session user aligned with API — never drop onboarding / setup flags. */
export function syncCurrentUserFromApi(apiUser: Record<string, unknown> | null | undefined): void {
  const normalized = normalizeApiUserRecord(apiUser);
  if (!normalized) {
    return;
  }

  const store = getSessionStore();
  const existing = store.currentUser as Record<string, unknown> | null;
  if (existing) {
    store.updateCurrentUser(normalized);
  } else {
    store.setCurrentUser(normalized);
  }
}

export function normalizePromptListResponse(promptsData: unknown): unknown[] {
  if (Array.isArray(promptsData)) {
    return promptsData;
  }
  if (
    promptsData &&
    typeof promptsData === 'object' &&
    Array.isArray((promptsData as { results?: unknown[] }).results)
  ) {
    return (promptsData as { results: unknown[] }).results;
  }
  return [];
}

export function resolvePromptCount(promptsData: unknown): number {
  return normalizePromptListResponse(promptsData).length;
}

/** True when profile is done but prompts have not been submitted yet. */
export function userNeedsPromptSetup(
  _promptCount: number,
  user?: Record<string, unknown> | null
): boolean {
  if (!user) {
    return true;
  }
  if (!isBasicProfileCompleted(user)) {
    return false;
  }
  return !isFullOnboardingCompleted(user);
}

/** Where to send the user immediately after a successful login. */
export function resolvePostLoginRoute(
  user: Record<string, unknown> | null | undefined,
  promptCount: number
): string {
  if (!user) {
    return '/';
  }

  const phase = resolveCandidateJourneyPhase(user, promptCount);
  if (phase === 'onboarding' || phase === 'prompt-setup') {
    return '/onboarding';
  }
  return '/dashboard';
}

/** Coerce v1 current-user fields to strings for form state. */
/** Merge API user with localStorage and normalize form field names. */
export function mergeProfileFormUser(
  apiUser: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  let stored: Record<string, unknown> = {};
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      stored = JSON.parse(raw) as Record<string, unknown>;
    }
  } catch {
    /* ignore */
  }

  const merged: Record<string, unknown> = {
    ...stored,
    ...(apiUser && typeof apiUser === 'object' ? apiUser : {}),
  };

  if ('skills' in merged) {
    merged.skills = normalizeSkillsList(merged.skills);
  }

  const phoneRaw = profileFieldAsString(merged.phoneNumber) || profileFieldAsString(merged.phone);
  if (phoneRaw) {
    const digits = phoneRaw.replace(/\D/g, '');
    merged.phoneNumber = digits ? `+${digits}` : '';
  }

  const dobRaw =
    profileFieldAsString(merged.dateOfBirth) || profileFieldAsString(merged.date_of_birth);
  if (dobRaw) {
    merged.dateOfBirth = dobRaw.length >= 10 ? dobRaw.slice(0, 10) : dobRaw;
  }

  const willing = merged.willingToRelocate ?? merged.willing_to_relocate;
  if (typeof willing === 'boolean') {
    merged.willingToRelocate = willing ? 'Yes' : 'No';
  }

  const work = profileFieldAsString(merged.workArrangements ?? merged.work_arrangements);
  if (work) {
    merged.workArrangements = work.charAt(0).toUpperCase() + work.slice(1).toLowerCase();
  }

  if (
    profileFieldAsString(merged.salary) === 'I prefer to negotiate' ||
    merged.preferNegotiate === 'Yes'
  ) {
    merged.preferNegotiate = 'Yes';
  }

  const resumeFileName =
    profileFieldAsString(merged.resumeFileName) || profileFieldAsString(merged.resume_filename);
  if (resumeFileName) {
    merged.resumeFileName = resumeFileName;
  }

  return merged;
}

export function profileFieldAsString(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

/** Incomplete basic profile OR unfinished onboarding → new Fast Onboarding. */
export function userNeedsOnboarding(user: Record<string, unknown> | null | undefined): boolean {
  if (!user) {
    return true;
  }
  return !isFullOnboardingCompleted(user);
}

/** Fast onboarding draft saved server-side — user should resume review, not restart. */
export function userHasFastOnboardingDraft(
  user: Record<string, unknown> | null | undefined
): boolean {
  if (!user) {
    return false;
  }
  const flow = String(user.onboardingFlow || '').trim();
  if (flow === 'fast' && !isFullOnboardingCompleted(user)) {
    return true;
  }
  return false;
}

/** Merge v1 user fields into localStorage after onboarding completes. */
export function persistCurrentUserPatch(patch: Record<string, unknown> | null | undefined): void {
  if (!patch || typeof patch !== 'object') {
    return;
  }
  try {
    const raw = localStorage.getItem('user');
    const prev = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const basicProfileDone =
      patch.basicProfileCompleted ??
      patch.basic_profile_completed ??
      prev.basicProfileCompleted ??
      prev.basic_profile_completed ??
      false;
    const onboardingDone =
      patch.onboardingCompleted ??
      patch.onboarding_completed ??
      prev.onboardingCompleted ??
      prev.onboarding_completed ??
      false;
    const needsOnboarding = !onboardingDone;
    localStorage.setItem(
      'user',
      JSON.stringify({
        ...prev,
        ...patch,
        basicProfileCompleted: basicProfileDone,
        basic_profile_completed: basicProfileDone,
        onboardingCompleted: onboardingDone,
        onboarding_completed: onboardingDone,
        needsOnboarding,
        needs_onboarding: needsOnboarding,
      })
    );
    const normalized = {
      ...patch,
      basicProfileCompleted: basicProfileDone,
      basic_profile_completed: basicProfileDone,
      onboardingCompleted: onboardingDone,
      onboarding_completed: onboardingDone,
      needsOnboarding,
      needs_onboarding: needsOnboarding,
    };
    const store = getSessionStore();
    const sessionUser = store.currentUser as Record<string, unknown> | null;
    if (sessionUser) {
      store.updateCurrentUser(normalized);
    } else {
      store.setCurrentUser({ ...prev, ...normalized });
    }
  } catch {
    /* ignore quota / parse errors */
  }
}

/** Job title shown on profile — supports Django snake_case and first prompt with a title. */
export function resolveDisplayJobTitle(
  prompts: unknown,
  userData?: Record<string, unknown> | null
): string {
  if (Array.isArray(prompts)) {
    for (const item of prompts) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      const title = String(row.jobTitle ?? row.job_title ?? '').trim();
      if (title) return title;
    }
  }

  if (userData) {
    const fromUser = String(
      userData.jobTitle ?? userData.job_title ?? userData.roleTitle ?? userData.role_title ?? ''
    ).trim();
    if (fromUser) return fromUser;

    const profession = userData.profession;
    if (profession && typeof profession === 'object') {
      const prof = profession as Record<string, unknown>;
      const name = String(prof.name ?? prof.title ?? prof.label ?? '').trim();
      if (name) return name;
    }
    if (typeof profession === 'string' && profession.trim()) {
      return profession.trim();
    }
  }

  return '';
}

export function resolveProfileImageUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
    return null;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }
  return null;
}
